import {Component, inject, Input, OnInit, signal} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {BusinessCallingApiService} from '@shared/services/api/conversation/business-calling-api.service';

/**
 * Meta chỉ cho Page gọi khách sau khi khách chấp thuận trong cửa sổ 7 ngày.
 * Dialog này là bước kiểm tra/xin quyền trước khi BusinessCallingService tạo WebRTC offer.
 */
@Component({
  selector: 'app-business-calling-permission-dialog',
  standalone: true,
  imports: [TranslocoPipe],
  template: `
    <div class="modal-header py-2 bg-light">
      <h6 class="modal-title">{{ 'common.callPhone' | transloco }}</h6>
      <button type="button" class="btn-close" [attr.aria-label]="'common.close' | transloco" (click)="activeModal.dismiss()"></button>
    </div>

    <div class="modal-body">
      <div class="alert alert-info mb-3">
        {{ 'conversation.businessCalling.permissionNotice' | transloco }}
      </div>

      @if (isChecking()) {
        <div class="d-flex justify-content-center align-items-center gap-2 py-2">
          <span class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></span>
          <span>{{ 'conversation.businessCalling.checkingPermission' | transloco }}</span>
        </div>
      } @else if (isGranted()) {
        <div class="alert alert-success mb-0">{{ 'conversation.businessCalling.permissionGranted' | transloco }}</div>
      } @else {
        <div [class.text-success]="!!successMessage()" [class.text-danger]="!successMessage()">
          {{ successMessage() || errorMessage() || ('conversation.businessCalling.permissionMissing' | transloco) }}
        </div>
      }
    </div>

    @if (isChecked() && !isGranted()) {
      <div class="modal-footer py-2">
        <button type="button" class="btn btn-primary" (click)="sendPermissionRequest()" [disabled]="isSending()">
          @if (isSending()) {
            <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
          }
          {{ 'conversation.businessCalling.requestPermission' | transloco }}
        </button>
      </div>
    }
  `,
})
export class BusinessCallingPermissionDialogComponent implements OnInit {
  @Input({required: true}) pageId = '';
  @Input({required: true}) pageUserId = '';

  readonly activeModal = inject(NgbActiveModal);
  private readonly api = inject(BusinessCallingApiService);
  private readonly transloco = inject(TranslocoService);

  readonly isChecking = signal(true);
  readonly isChecked = signal(false);
  readonly isGranted = signal(false);
  readonly isSending = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');

  ngOnInit(): void {
    this.checkPermission();
  }

  private checkPermission(): void {
    this.isChecking.set(true);
    this.isChecked.set(false);
    this.api.checkCallPermission(this.params()).subscribe({
      next: res => {
        const granted = Number(res?.code) === 1 && res?.data?.status === 'HAS_PERMISSION';
        this.isGranted.set(granted);
        this.isChecked.set(true);
        this.isChecking.set(false);
        this.errorMessage.set(granted ? '' : this.getResponseMessage(res?.messages));
        if (granted) {
          setTimeout(() => this.activeModal.close(true), 250);
        }
      },
      error: () => {
        this.isGranted.set(false);
        this.isChecked.set(true);
        this.isChecking.set(false);
        this.errorMessage.set(this.t('conversation.businessCalling.checkPermissionError'));
      },
    });
  }

  sendPermissionRequest(): void {
    if (this.isSending()) return;

    this.isSending.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.requestCallPermission(this.params()).subscribe({
      next: res => {
        this.isSending.set(false);
        if (Number(res?.code) === 1) {
          this.successMessage.set(this.t('conversation.businessCalling.requestPermissionSent'));
          return;
        }
        this.errorMessage.set(this.getResponseMessage(res?.messages) || this.t('conversation.businessCalling.requestPermissionError'));
      },
      error: () => {
        this.isSending.set(false);
        this.errorMessage.set(this.t('conversation.businessCalling.requestPermissionError'));
      },
    });
  }

  private params(): { pageId: string; recipientId: string } {
    return {pageId: String(this.pageId), recipientId: String(this.pageUserId)};
  }

  private getResponseMessage(messages: unknown): string {
    if (typeof messages === 'string') return messages;
    if (messages && typeof messages === 'object') return Object.values(messages as Record<string, unknown>)
      .filter((value): value is string => typeof value === 'string')
      .join(' ');
    return '';
  }

  private t(key: string): string {
    return this.transloco.translate(key);
  }
}
